import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getCountryNameFromISO } from '../utils/countryUtils.js';
import { validatePhoneNumber, getPhonePlaceholder, formatFullPhoneNumber } from '../utils/phoneValidation.js';
import './ProfileVerificationModal.css';
import './RequestAccessModal.css'; // For ProfileEmailVerification styling to match original

const ProfileVerificationModal = ({ onComplete, onClose }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  
  const [step, setStep] = useState('checking'); // 'checking', 'phone', 'email-input', 'email-verify', 'complete'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [verificationCode, setVerificationCode] = useState('');
  
  // Country code autocomplete states
  const [countryCodeInput, setCountryCodeInput] = useState('+44');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState([]);
  
  // Status tracking
  const [verificationStatus, setVerificationStatus] = useState({
    hasEmail: false,
    isEmailVerified: false,
    hasPhone: false,
    isPhoneVerified: false
  });

  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  // Check what verification is needed
  useEffect(() => {
    checkVerificationStatus();
  }, []);

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest('.country-code-autocomplete')) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  const apiCall = async (endpoint, options = {}) => {
    const token = await getAccessTokenSilently();
    const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
    const cleanApiUrl = apiUrl.replace(/\/$/, '');
    
    const response = await fetch(`${cleanApiUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }
    
    return response.json();
  };

  const checkVerificationStatus = async () => {
    try {
      setLoading(true);
      const result = await apiCall('/api/verification/status');
      
      setVerificationStatus(result.status);
      
      if (!result.needsVerification) {
        setStep('complete');
        return;
      }
      
      // Determine first step needed
      if (!result.status.hasPhone) {
        setStep('phone');
      } else if (!result.status.hasEmail) {
        setStep('email-input');
      } else if (!result.status.isEmailVerified) {
        setStep('email-verify');
        // Send verification code immediately
        sendEmailVerification();
      } else {
        setStep('complete');
      }
    } catch (error) {
      setError('Failed to check verification status');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async () => {
    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    // Validate phone number format for the selected country
    const validation = validatePhoneNumber(phoneNumber, countryCodeInput);
    if (!validation.valid) {
      setError(validation.error + (validation.hint ? ` (${validation.hint})` : ''));
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const fullPhoneNumber = formatFullPhoneNumber(countryCodeInput, phoneNumber);
      
      // Find the selected country from the country code
      const selectedCountryData = countries.find(country => country.code === countryCodeInput);
      let countryName = null;
      try {
        countryName = selectedCountryData ? getCountryNameFromISO(selectedCountryData.iso) : null;
      } catch (e) {
        countryName = selectedCountryData?.name || null;
      }
      
      await apiCall('/api/verification/phone/update', {
        method: 'POST',
        body: JSON.stringify({ 
          phoneNumber: fullPhoneNumber,
          country: countryName
        })
      });
      
      setSuccess('Phone number updated successfully');
      
      // Move to next step
      setTimeout(() => {
        setSuccess('');
        if (!verificationStatus.hasEmail) {
          setStep('email-input');
        } else if (!verificationStatus.isEmailVerified) {
          setStep('email-verify');
          sendEmailVerification();
        } else {
          setStep('complete');
        }
      }, 1500);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await sendEmailVerification();
      setStep('email-verify');
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const sendEmailVerification = async () => {
    try {
      await apiCall('/api/verification/email/send', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      
      setSuccess('Verification code sent to your email');
      setCanResend(false);
      setResendTimer(120); // 2 minutes
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCodeVerification = async () => {
    if (!verificationCode.trim()) {
      setError('Verification code is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await apiCall('/api/verification/email/verify', {
        method: 'POST',
        body: JSON.stringify({ email, code: verificationCode })
      });
      
      setSuccess('Email verified successfully!');
      
      setTimeout(() => {
        setStep('complete');
      }, 1500);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    
    try {
      setLoading(true);
      setError('');
      setVerificationCode(''); // Clear old code when resending
      
      await apiCall('/api/verification/email/resend', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      
      setSuccess('New verification code sent');
      setCanResend(false);
      setResendTimer(120);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  // Helper function to generate flag from ISO country code
  const getFlagFromISO = (isoCode) => {
    return isoCode.toUpperCase().replace(/./g, char => 
      String.fromCodePoint(127397 + char.charCodeAt())
    );
  };

  // Country mapping with proper Unicode flags
  const countries = [
    { code: '+1', iso: 'US', flag: getFlagFromISO('US'), name: 'United States' },
    { code: '+1', iso: 'CA', flag: getFlagFromISO('CA'), name: 'Canada' },
    { code: '+44', iso: 'GB', flag: getFlagFromISO('GB'), name: 'United Kingdom' },
    { code: '+33', iso: 'FR', flag: getFlagFromISO('FR') },
    { code: '+49', iso: 'DE', flag: getFlagFromISO('DE') },
    { code: '+39', iso: 'IT', flag: getFlagFromISO('IT') },
    { code: '+34', iso: 'ES', flag: getFlagFromISO('ES') },
    { code: '+31', iso: 'NL', flag: getFlagFromISO('NL') },
    { code: '+32', iso: 'BE', flag: getFlagFromISO('BE') },
    { code: '+41', iso: 'CH', flag: getFlagFromISO('CH') },
    { code: '+43', iso: 'AT', flag: getFlagFromISO('AT') },
    { code: '+45', iso: 'DK', flag: getFlagFromISO('DK') },
    { code: '+46', iso: 'SE', flag: getFlagFromISO('SE') },
    { code: '+47', iso: 'NO', flag: getFlagFromISO('NO') },
    { code: '+358', iso: 'FI', flag: getFlagFromISO('FI') },
    { code: '+353', iso: 'IE', flag: getFlagFromISO('IE') },
    { code: '+351', iso: 'PT', flag: getFlagFromISO('PT') },
    { code: '+30', iso: 'GR', flag: getFlagFromISO('GR') },
    { code: '+48', iso: 'PL', flag: getFlagFromISO('PL') },
    { code: '+420', iso: 'CZ', flag: getFlagFromISO('CZ') },
    { code: '+421', iso: 'SK', flag: getFlagFromISO('SK') },
    { code: '+36', iso: 'HU', flag: getFlagFromISO('HU') },
    { code: '+40', iso: 'RO', flag: getFlagFromISO('RO') },
    { code: '+359', iso: 'BG', flag: getFlagFromISO('BG') },
    { code: '+385', iso: 'HR', flag: getFlagFromISO('HR') },
    { code: '+386', iso: 'SI', flag: getFlagFromISO('SI') },
    { code: '+372', iso: 'EE', flag: getFlagFromISO('EE') },
    { code: '+371', iso: 'LV', flag: getFlagFromISO('LV') },
    { code: '+370', iso: 'LT', flag: getFlagFromISO('LT') },
    { code: '+7', iso: 'RU', flag: getFlagFromISO('RU') },
    { code: '+380', iso: 'UA', flag: getFlagFromISO('UA') },
    { code: '+375', iso: 'BY', flag: getFlagFromISO('BY') },
    { code: '+373', iso: 'MD', flag: getFlagFromISO('MD') },
    { code: '+381', iso: 'RS', flag: getFlagFromISO('RS') },
    { code: '+382', iso: 'ME', flag: getFlagFromISO('ME') },
    { code: '+383', iso: 'XK', flag: getFlagFromISO('XK') },
    { code: '+387', iso: 'BA', flag: getFlagFromISO('BA') },
    { code: '+389', iso: 'MK', flag: getFlagFromISO('MK') },
    { code: '+355', iso: 'AL', flag: getFlagFromISO('AL') },
    { code: '+90', iso: 'TR', flag: getFlagFromISO('TR') },
    { code: '+357', iso: 'CY', flag: getFlagFromISO('CY') },
    { code: '+356', iso: 'MT', flag: getFlagFromISO('MT') },
    { code: '+354', iso: 'IS', flag: getFlagFromISO('IS') },
    { code: '+298', iso: 'FO', flag: getFlagFromISO('FO') },
    { code: '+299', iso: 'GL', flag: getFlagFromISO('GL') },
    { code: '+376', iso: 'AD', flag: getFlagFromISO('AD') },
    { code: '+377', iso: 'MC', flag: getFlagFromISO('MC') },
    { code: '+378', iso: 'SM', flag: getFlagFromISO('SM') },
    { code: '+39', iso: 'VA', flag: getFlagFromISO('VA') },
    { code: '+423', iso: 'LI', flag: getFlagFromISO('LI') },
    { code: '+33', iso: 'FR', flag: getFlagFromISO('FR') },
    { code: '+86', iso: 'CN', flag: getFlagFromISO('CN') },
    { code: '+91', iso: 'IN', flag: getFlagFromISO('IN') },
    { code: '+81', iso: 'JP', flag: getFlagFromISO('JP') },
    { code: '+82', iso: 'KR', flag: getFlagFromISO('KR') },
    { code: '+62', iso: 'ID', flag: getFlagFromISO('ID') },
    { code: '+60', iso: 'MY', flag: getFlagFromISO('MY') },
    { code: '+65', iso: 'SG', flag: getFlagFromISO('SG') },
    { code: '+66', iso: 'TH', flag: getFlagFromISO('TH') },
    { code: '+84', iso: 'VN', flag: getFlagFromISO('VN') },
    { code: '+63', iso: 'PH', flag: getFlagFromISO('PH') },
    { code: '+61', iso: 'AU', flag: getFlagFromISO('AU') },
    { code: '+64', iso: 'NZ', flag: getFlagFromISO('NZ') },
    { code: '+852', iso: 'HK', flag: getFlagFromISO('HK') },
    { code: '+853', iso: 'MO', flag: getFlagFromISO('MO') },
    { code: '+886', iso: 'TW', flag: getFlagFromISO('TW') },
    { code: '+20', iso: 'EG', flag: getFlagFromISO('EG') },
    { code: '+27', iso: 'ZA', flag: getFlagFromISO('ZA') },
    { code: '+234', iso: 'NG', flag: getFlagFromISO('NG') },
    { code: '+254', iso: 'KE', flag: getFlagFromISO('KE') },
    { code: '+233', iso: 'GH', flag: getFlagFromISO('GH') },
    { code: '+212', iso: 'MA', flag: getFlagFromISO('MA') },
    { code: '+213', iso: 'DZ', flag: getFlagFromISO('DZ') },
    { code: '+216', iso: 'TN', flag: getFlagFromISO('TN') },
    { code: '+218', iso: 'LY', flag: getFlagFromISO('LY') },
    { code: '+249', iso: 'SD', flag: getFlagFromISO('SD') },
    { code: '+251', iso: 'ET', flag: getFlagFromISO('ET') },
    { code: '+256', iso: 'UG', flag: getFlagFromISO('UG') },
    { code: '+255', iso: 'TZ', flag: getFlagFromISO('TZ') },
    { code: '+250', iso: 'RW', flag: getFlagFromISO('RW') },
    { code: '+257', iso: 'BI', flag: getFlagFromISO('BI') },
    { code: '+243', iso: 'CD', flag: getFlagFromISO('CD') },
    { code: '+242', iso: 'CG', flag: getFlagFromISO('CG') },
    { code: '+236', iso: 'CF', flag: getFlagFromISO('CF') },
    { code: '+235', iso: 'TD', flag: getFlagFromISO('TD') },
    { code: '+237', iso: 'CM', flag: getFlagFromISO('CM') },
    { code: '+240', iso: 'GQ', flag: getFlagFromISO('GQ') },
    { code: '+241', iso: 'GA', flag: getFlagFromISO('GA') },
    { code: '+239', iso: 'ST', flag: getFlagFromISO('ST') },
    { code: '+238', iso: 'CV', flag: getFlagFromISO('CV') },
    { code: '+221', iso: 'SN', flag: getFlagFromISO('SN') },
    { code: '+220', iso: 'GM', flag: getFlagFromISO('GM') },
    { code: '+224', iso: 'GN', flag: getFlagFromISO('GN') },
    { code: '+245', iso: 'GW', flag: getFlagFromISO('GW') },
    { code: '+232', iso: 'SL', flag: getFlagFromISO('SL') },
    { code: '+231', iso: 'LR', flag: getFlagFromISO('LR') },
    { code: '+225', iso: 'CI', flag: getFlagFromISO('CI') },
    { code: '+226', iso: 'BF', flag: getFlagFromISO('BF') },
    { code: '+223', iso: 'ML', flag: getFlagFromISO('ML') },
    { code: '+227', iso: 'NE', flag: getFlagFromISO('NE') },
    { code: '+229', iso: 'BJ', flag: getFlagFromISO('BJ') },
    { code: '+228', iso: 'TG', flag: getFlagFromISO('TG') },
    { code: '+230', iso: 'MU', flag: getFlagFromISO('MU') },
    { code: '+248', iso: 'SC', flag: getFlagFromISO('SC') },
    { code: '+261', iso: 'MG', flag: getFlagFromISO('MG') },
    { code: '+269', iso: 'KM', flag: getFlagFromISO('KM') },
    { code: '+262', iso: 'RE', flag: getFlagFromISO('RE') },
    { code: '+262', iso: 'YT', flag: getFlagFromISO('YT') },
    { code: '+290', iso: 'SH', flag: getFlagFromISO('SH') },
    { code: '+247', iso: 'AC', flag: getFlagFromISO('AC') },
    { code: '+290', iso: 'TA', flag: getFlagFromISO('TA') },
    { code: '+52', iso: 'MX', flag: getFlagFromISO('MX') },
    { code: '+54', iso: 'AR', flag: getFlagFromISO('AR') },
    { code: '+55', iso: 'BR', flag: getFlagFromISO('BR') },
    { code: '+56', iso: 'CL', flag: getFlagFromISO('CL') },
    { code: '+57', iso: 'CO', flag: getFlagFromISO('CO') },
    { code: '+51', iso: 'PE', flag: getFlagFromISO('PE') },
    { code: '+58', iso: 'VE', flag: getFlagFromISO('VE') },
    { code: '+593', iso: 'EC', flag: getFlagFromISO('EC') },
    { code: '+591', iso: 'BO', flag: getFlagFromISO('BO') },
    { code: '+595', iso: 'PY', flag: getFlagFromISO('PY') },
    { code: '+598', iso: 'UY', flag: getFlagFromISO('UY') },
    { code: '+592', iso: 'GY', flag: getFlagFromISO('GY') },
    { code: '+597', iso: 'SR', flag: getFlagFromISO('SR') },
    { code: '+594', iso: 'GF', flag: getFlagFromISO('GF') },
    { code: '+508', iso: 'PM', flag: getFlagFromISO('PM') },
    { code: '+590', iso: 'GP', flag: getFlagFromISO('GP') },
    { code: '+596', iso: 'MQ', flag: getFlagFromISO('MQ') },
    { code: '+599', iso: 'CW', flag: getFlagFromISO('CW') },
    { code: '+599', iso: 'BQ', flag: getFlagFromISO('BQ') },
    { code: '+297', iso: 'AW', flag: getFlagFromISO('AW') },
    { code: '+1', iso: 'CA', flag: getFlagFromISO('CA') },
    { code: '+502', iso: 'GT', flag: getFlagFromISO('GT') },
    { code: '+503', iso: 'SV', flag: getFlagFromISO('SV') },
    { code: '+504', iso: 'HN', flag: getFlagFromISO('HN') },
    { code: '+505', iso: 'NI', flag: getFlagFromISO('NI') },
    { code: '+506', iso: 'CR', flag: getFlagFromISO('CR') },
    { code: '+507', iso: 'PA', flag: getFlagFromISO('PA') },
    { code: '+501', iso: 'BZ', flag: getFlagFromISO('BZ') },
    { code: '+1', iso: 'JM', flag: getFlagFromISO('JM') },
    { code: '+1', iso: 'CU', flag: getFlagFromISO('CU') },
    { code: '+509', iso: 'HT', flag: getFlagFromISO('HT') },
    { code: '+1', iso: 'DO', flag: getFlagFromISO('DO') },
    { code: '+1', iso: 'PR', flag: getFlagFromISO('PR') },
    { code: '+1', iso: 'TT', flag: getFlagFromISO('TT') },
    { code: '+1', iso: 'BB', flag: getFlagFromISO('BB') },
    { code: '+1', iso: 'GD', flag: getFlagFromISO('GD') },
    { code: '+1', iso: 'LC', flag: getFlagFromISO('LC') },
    { code: '+1', iso: 'VC', flag: getFlagFromISO('VC') },
    { code: '+1', iso: 'AG', flag: getFlagFromISO('AG') },
    { code: '+1', iso: 'DM', flag: getFlagFromISO('DM') },
    { code: '+1', iso: 'KN', flag: getFlagFromISO('KN') },
    { code: '+1', iso: 'BS', flag: getFlagFromISO('BS') },
    { code: '+53', iso: 'CU', flag: getFlagFromISO('CU') },
    { code: '+972', iso: 'IL', flag: getFlagFromISO('IL') },
    { code: '+973', iso: 'BH', flag: getFlagFromISO('BH') },
    { code: '+974', iso: 'QA', flag: getFlagFromISO('QA') },
    { code: '+971', iso: 'AE', flag: getFlagFromISO('AE') },
    { code: '+968', iso: 'OM', flag: getFlagFromISO('OM') },
    { code: '+965', iso: 'KW', flag: getFlagFromISO('KW') },
    { code: '+966', iso: 'SA', flag: getFlagFromISO('SA') },
    { code: '+967', iso: 'YE', flag: getFlagFromISO('YE') },
    { code: '+964', iso: 'IQ', flag: getFlagFromISO('IQ') },
    { code: '+963', iso: 'SY', flag: getFlagFromISO('SY') },
    { code: '+961', iso: 'LB', flag: getFlagFromISO('LB') },
    { code: '+962', iso: 'JO', flag: getFlagFromISO('JO') },
    { code: '+970', iso: 'PS', flag: getFlagFromISO('PS') },
    { code: '+98', iso: 'IR', flag: getFlagFromISO('IR') },
    { code: '+93', iso: 'AF', flag: getFlagFromISO('AF') },
    { code: '+92', iso: 'PK', flag: getFlagFromISO('PK') },
    { code: '+880', iso: 'BD', flag: getFlagFromISO('BD') },
    { code: '+94', iso: 'LK', flag: getFlagFromISO('LK') },
    { code: '+960', iso: 'MV', flag: getFlagFromISO('MV') },
    { code: '+977', iso: 'NP', flag: getFlagFromISO('NP') },
    { code: '+975', iso: 'BT', flag: getFlagFromISO('BT') },
    { code: '+95', iso: 'MM', flag: getFlagFromISO('MM') },
    { code: '+856', iso: 'LA', flag: getFlagFromISO('LA') },
    { code: '+855', iso: 'KH', flag: getFlagFromISO('KH') },
    { code: '+673', iso: 'BN', flag: getFlagFromISO('BN') },
    { code: '+670', iso: 'TL', flag: getFlagFromISO('TL') },
    { code: '+678', iso: 'VU', flag: getFlagFromISO('VU') },
    { code: '+679', iso: 'FJ', flag: getFlagFromISO('FJ') },
    { code: '+685', iso: 'WS', flag: getFlagFromISO('WS') },
    { code: '+676', iso: 'TO', flag: getFlagFromISO('TO') },
    { code: '+687', iso: 'NC', flag: getFlagFromISO('NC') },
    { code: '+689', iso: 'PF', flag: getFlagFromISO('PF') },
    { code: '+690', iso: 'TK', flag: getFlagFromISO('TK') },
    { code: '+691', iso: 'FM', flag: getFlagFromISO('FM') },
    { code: '+692', iso: 'MH', flag: getFlagFromISO('MH') },
    { code: '+680', iso: 'PW', flag: getFlagFromISO('PW') },
    { code: '+683', iso: 'NU', flag: getFlagFromISO('NU') },
    { code: '+684', iso: 'AS', flag: getFlagFromISO('AS') },
    { code: '+681', iso: 'WF', flag: getFlagFromISO('WF') },
    { code: '+682', iso: 'CK', flag: getFlagFromISO('CK') },
    { code: '+686', iso: 'KI', flag: getFlagFromISO('KI') },
    { code: '+688', iso: 'TV', flag: getFlagFromISO('TV') },
    { code: '+677', iso: 'SB', flag: getFlagFromISO('SB') },
    { code: '+675', iso: 'PG', flag: getFlagFromISO('PG') },
    { code: '+674', iso: 'NR', flag: getFlagFromISO('NR') }
  ];

  // Filter countries based on user input
  const filterCountries = (input) => {
    if (!input || input.trim() === '') return [];
    
    const searchTerm = input.toLowerCase().trim();
    const searchTermClean = searchTerm.replace(/[^a-z0-9+]/g, '');
    const termWithPlus = searchTermClean.startsWith('+') ? searchTermClean : '+' + searchTermClean.replace(/^\+*/, '');
    
    // Get matches
    const matches = countries.filter(country => {
      const code = country.code.toLowerCase().replace(/[^a-z0-9+]/g, '');
      const iso = country.iso.toLowerCase();
      const name = (country.name || '').toLowerCase();
      
      return code.includes(searchTermClean) || 
             iso.includes(searchTermClean) || 
             name.includes(searchTerm);
    });
    
    // Sort: exact code matches first, then codes starting with input, then by length
    matches.sort((a, b) => {
      const aCode = a.code.toLowerCase();
      const bCode = b.code.toLowerCase();
      
      // Exact match gets highest priority
      const aExact = aCode === termWithPlus;
      const bExact = bCode === termWithPlus;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Code starting with search term gets second priority
      const aStartsWith = aCode.startsWith(termWithPlus);
      const bStartsWith = bCode.startsWith(termWithPlus);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      // If both start with the term, shorter code = closer match
      if (aStartsWith && bStartsWith) {
        return aCode.length - bCode.length;
      }
      
      // Otherwise sort by code length (shorter = more common)
      return aCode.length - bCode.length;
    });
    
    return matches.slice(0, 5);
  };

  // Handle country code input changes  
  const handleCountryCodeInput = (value) => {
    setCountryCodeInput(value);
    
    if (value.trim()) {
      const filtered = filterCountries(value);
      setFilteredCountries(filtered);
      setShowCountryDropdown(filtered.length > 0);
    } else {
      setFilteredCountries([]);
      setShowCountryDropdown(false);
    }
  };

  // Handle country selection from dropdown
  const selectCountry = (countryData) => {
    setCountryCodeInput(countryData.code);
    setShowCountryDropdown(false);
    setFilteredCountries([]);
  };

  if (step === 'checking') {
    return (
      <div className="verification-modal">
        <div className="verification-content">
          <div className="verification-header">
            <h2>Checking Profile...</h2>
          </div>
          <div className="verification-loading">
            <div className="loader"></div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className="verification-modal">
        <div className="verification-content">
          <div className="verification-header">
            <h2>Profile Complete!</h2>
            <p>Your profile information has been verified.</p>
          </div>
          <button 
            className="verification-btn primary"
            onClick={handleComplete}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <div className="verification-modal">
      <div className="verification-content">
        <div className="verification-header">
          <h2>Complete Your Profile</h2>
          <p>We need to verify some information to continue</p>
        </div>

        {error && <div className="verification-error">{error}</div>}
        {success && <div className="verification-success">{success}</div>}

        {step === 'phone' && (
          <div className="verification-step">
            <h3>Phone Number</h3>
            <p>Please provide your phone number</p>
            <div className="phone-input-container">
              <div className="phone-flex-container">
                <div className="country-code-autocomplete">
                  <input
                    type="text"
                    value={countryCodeInput}
                    onChange={(e) => handleCountryCodeInput(e.target.value)}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Country code"
                    className="country-code-input"
                  />
                  {showCountryDropdown && filteredCountries.length > 0 && (
                    <div className="country-dropdown">
                      {filteredCountries.slice(0, 5).map((country, index) => (
                        <div
                          key={`${country.code}-${country.iso}-${index}`}
                          className="country-option"
                          onClick={() => selectCountry(country)}
                        >
                          <span className="country-flag">{country.flag}</span>
                          <span className="country-code">{country.code}</span>
                          {country.name && <span className="country-name">{country.name}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  placeholder={getPhonePlaceholder(countryCodeInput)}
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d\s\-\(\)]/g, '');
                    setPhoneNumber(value);
                    // Clear error when user types
                    if (error) setError('');
                  }}
                  className="verification-input phone-number-input"
                />
              </div>
            </div>
            &nbsp;<button 
              onClick={handlePhoneSubmit}
              disabled={loading}
              className="verification-btn primary"
            >
              Continue
            </button>
          </div>
        )}

        {step === 'email-input' && (
          <div className="verification-step">
            <h3>Email Address</h3>
            <p>Please confirm or update your email address</p>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="verification-input"
            />
            <button 
              onClick={handleEmailSubmit}
              disabled={loading}
              className="verification-btn primary"
            >
              Send Verification Code
            </button>
          </div>
        )}

        {step === 'email-verify' && (
          <div className="verification-step">
            <h3>Verify Email</h3>
            <p>Enter the 6-digit code sent to: <strong>{email}</strong></p>
            <input
              type="text"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="verification-input code-input"
              maxLength="6"
            />
            <button 
              onClick={handleCodeVerification}
              disabled={loading}
              className="verification-btn primary"
            >
              Verify Email
            </button>
            
            <div className="verification-resend">
              {canResend ? (
                <button 
                  onClick={handleResendCode}
                  className="verification-btn secondary"
                  disabled={loading}
                >
                  Resend Code
                </button>
              ) : (
                <span className="resend-timer">
                  Resend available in {Math.floor(resendTimer / 60)}:{(resendTimer % 60).toString().padStart(2, '0')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

// Profile Email Verification Component for email updates in profile settings
export const ProfileEmailVerification = ({ currentEmail, newEmail, onSuccess, onCancel }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [step, setStep] = useState('request'); // 'request', 'verify'
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const apiCall = async (endpoint, options = {}) => {
    const token = await getAccessTokenSilently();
    const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
    const cleanApiUrl = apiUrl.replace(/\/$/, '');
    
    const response = await fetch(`${cleanApiUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }
    
    return response.json();
  };

  const handleVerificationRequest = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      await apiCall('/api/verification/email/send', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail })
      });
      
      setStep('verify');
    } catch (error) {
      setError(error.message || 'Failed to send verification email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeVerification = async () => {
    if (isLoading || verificationCode.length !== 6) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      await apiCall('/api/verification/email/verify', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail, code: verificationCode })
      });
      
      onSuccess(newEmail);
    } catch (error) {
      setError(error.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <div className="request-access-modal">
        <div className="modal-content">
          <button className="close-button" onClick={onCancel}>
            ✖
          </button>
          
          {step === 'request' ? (
            <div className="verification-step">
              <div className="email-request">
                <h2 className="modal-h2">Verify Email Change</h2>
                <p className="modalText">
                  You're changing your email address from:
                </p>
                <p className="modalText">
                  <b>{currentEmail || 'Not set'}</b> → <b style={{color: '#4f93ff'}}>{newEmail}</b>
                </p>
                <p className="modalText2">
                  We'll send a verification code to your new email address.
                </p>
                {error && <p className="error-message">{error}</p>}
                <button 
                  onClick={handleVerificationRequest} 
                  className="blueButt" 
                  disabled={isLoading}
                >
                  Send Verification Code
                </button>
              </div>
            </div>
          ) : (
            <div className="verification-step">
              <div className="email-verify">
                <h2 className="modal-h2">Enter Verification Code</h2>
                <p className="modalText">
                  We have just sent you an email with your unique code to: <b>{newEmail}</b>  
                  &nbsp; Please enter it below to verify your email:
                </p>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="verification-input code-input"
                  maxLength="6"
                  autoFocus
                />
                {error && <p className="error-message">{error}</p>}
                <button 
                  onClick={handleCodeVerification} 
                  className="blueButt" 
                  disabled={isLoading || verificationCode.length !== 6}
                >
                  Verify Code
                </button>
                <button 
                  onClick={() => {
                    setStep('request');
                    setVerificationCode('');
                    setError('');
                  }} 
                  className="text-button link-button"
                >
                  Resend Code
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileVerificationModal;
